export function isString(value: unknown): value is string {
  return typeof value === 'string';
}

export function isNumber(value: unknown): value is number {
  return typeof value === 'number';
}

export function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

export function isUnknown(_: unknown): _ is unknown {
  return true;
}

export function isNullOr<T>(isT: (value: unknown) => value is T) {
  return (value: unknown): value is T | null => value === null || isT(value);
}

export function isUndefinedOr<T>(isT: (value: unknown) => value is T) {
  return (value: unknown): value is T | undefined => value === undefined || isT(value);
}

export function isNullable<T>(isT: (value: unknown) => value is T) {
  return (value: unknown): value is T | undefined | null => value === undefined || value === null || isT(value);
}

export function isDefined<T>(value: T | undefined): value is T {
  return value !== undefined;
}

export type ObjectSpec<T extends object> = {
  [K in keyof T]: (value: unknown) => value is T[K];
};

export function conformsTo<T extends object>(spec: ObjectSpec<T>) {
  return (value: unknown): value is T => {
    if (typeof value !== 'object' || value === null) {
      return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return Object.entries(spec).every(([key, guard]: [any, any]) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return guard((value as any)[key]);
    });
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isRecordWith<K extends keyof any, V>(
  isKey: (key: unknown) => key is K,
  isValue: (val: unknown) => val is V,
) {
  return (value: unknown): value is Record<K, V> => {
    if (typeof value !== 'object' || value === null) {
      return false;
    }

    return Object.entries(value).every(([key, val]) => isKey(key) && isValue(val));
  };
}

export function isArrayAndEveryElement<T>(isElement: (element: unknown) => element is T) {
  return (array: unknown): array is T[] => {
    return Array.isArray(array) && array.every(isElement);
  };
}

export function matchesOneOf<A, B>(isA: (element: unknown) => element is A, isB: (element: unknown) => element is B) {
  return (value: unknown): value is A | B => {
    return isA(value) || isB(value);
  };
}
